import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { usePermanentLocations, usePermanentRealtimeDealers } from "components";
import { PAIR_USERS_OPTIONS } from "modules/UsersDashboard/components";
import { useDmsUsersQuery } from "modules/UsersDashboard/components/PairUsers/hooks";
import { useGetUsersDashboardFilters } from "modules/UsersDashboard/components/UsersDashboardFilters/hooks";
import { useUserRolesQuery, useVersionsQuery } from "modules/UsersDashboard/hooks";

import type { DropdownItemProps } from "components/Dropdown";
import type { TFunction } from "i18next";
import type { Dealer } from "models";

export enum USER_STATUSES {
  All = 0,
  Active,
  Inactive,
  Deleted
}

export enum USER_TYPES {
  All = 0,
  Combi,
  Regular,
  Dpo
}

interface PairUsersSelectOption<T extends number | string> extends DropdownItemProps<T> {
  disabled: boolean;
}

const getVersionOptions = (versions?: string[]): DropdownItemProps<string>[] => versions?.map(version => ({ key: version, value: version, text: version })) || [];

export const useGetVersionsOptions = () => {
  const { data } = useVersionsQuery();
  return { webVersionOptions: getVersionOptions(data?.web_versions), appVersionOptions: getVersionOptions(data?.app_versions) };
};

export const useGetPairingUsersOptions = (): { pairUsersOptions: PairUsersSelectOption<number>[] } => {
  const { t } = useTranslation();

  const { dealers } = usePermanentRealtimeDealers();
  const { locations } = usePermanentLocations();
  const {
    filters: { dealer_ids, dealer_location_ids }
  } = useGetUsersDashboardFilters();

  const pairUsersOptions = () => {
    if (dealer_ids.length > 1) return [];

    const locationsDealerId = locations.find(loc => loc.id === dealer_location_ids[0])?.dealer_id;
    const selectedDealer = dealers.find(dealer => dealer.id === locationsDealerId);

    return [
      {
        key: PAIR_USERS_OPTIONS.OneIAutomotive,
        text: t("v8_pair_to_1i_automotive", "Pair to 1iAutomotive"),
        value: PAIR_USERS_OPTIONS.OneIAutomotive,
        disabled: Boolean(selectedDealer?.onei_planning_enabled) == false
      },
      {
        key: PAIR_USERS_OPTIONS.PlanIt,
        text: t("v8_pair_to_planit", "Pair to PlanIT"),
        value: PAIR_USERS_OPTIONS.PlanIt,
        disabled: Boolean(selectedDealer?.planit_planning_enabled) == false
      },
      { key: PAIR_USERS_OPTIONS.Dms, text: t("v8_pair_to_dms", "Pair to DMS"), value: PAIR_USERS_OPTIONS.Dms, disabled: false }
    ];
  };

  return { pairUsersOptions: dealer_ids.length > 1 ? [] : pairUsersOptions() };
};

type UserDropdownOptions = {
  userStatusOptions: DropdownItemProps[];
  userTypeOptions: DropdownItemProps[];
};

export const getUserDropdownOptions = (t: TFunction): UserDropdownOptions => ({
  userStatusOptions: [
    { key: USER_STATUSES.All, value: USER_STATUSES.All, text: t("v8_all", "All") },
    { key: USER_STATUSES.Active, value: USER_STATUSES.Active, text: t("v8_active", "Active") },
    { key: USER_STATUSES.Inactive, value: USER_STATUSES.Inactive, text: t("v8_inactive", "Inactive") }
  ],
  userTypeOptions: [
    { key: USER_TYPES.All, value: USER_TYPES.All, text: t("v8_all", "All") },
    { key: USER_TYPES.Combi, value: USER_TYPES.Combi, text: t("v8_combi_users", "Combi Users") },
    { key: USER_TYPES.Regular, value: USER_TYPES.Regular, text: t("v8_regular_users", "Regular Users") },
    { key: USER_TYPES.Dpo, value: USER_TYPES.Dpo, text: t("v8_dpo", "DPO") }
  ]
});

type UserRoleOptions = {
  userRolesOptions: DropdownItemProps[];
};

export const useGetUserRoleOptions = (): UserRoleOptions => {
  const { data } = useUserRolesQuery();
  return { userRolesOptions: data?.map(({ id, name }) => ({ key: id, value: id, text: name })) || [] };
};

export const useGetDmsUsersOptions = (dealerLocationId: number, currentUserId?: number | null) => {
  const { data, isLoading, refetch } = useDmsUsersQuery(dealerLocationId);

  useEffect(() => {
    if (dealerLocationId > 0) refetch();
  }, [dealerLocationId]);

  const dmsUserOptions: DropdownItemProps<string>[] =
    data?.map(({ dms_nr, name, user_id }) => ({ key: dms_nr, value: dms_nr, text: name, disabled: !!user_id && user_id !== currentUserId })) ?? [];
  return { dmsUserOptions, isDmsUsersOptionsLoading: isLoading };
};

type GetAllowedLocationsParams = {
  dealers: Dealer[];
  dealerIds: number[];
};

type GetAllowedLocationsReturn = {
  warrantyRoleAllowedLocationsOptions: DropdownItemProps<number | string>[];
};

export const getAllowedLocations = ({ dealers, dealerIds }: GetAllowedLocationsParams): GetAllowedLocationsReturn => {
  const warrantyRoleAllowedLocationsOptions: DropdownItemProps<number | string>[] = [];

  const selectedDealers = dealerIds
    .map(dealerId => dealers.find(dealer => dealer.id === dealerId))
    .filter(dealer => dealer !== undefined)
    .filter(dealer => dealer.active)
    .sort((a, b) => a.name.localeCompare(b.name));

  selectedDealers.forEach(dealer => {
    const dealerObject = { key: `allowed-locations-${dealer.id}`, value: dealer.id, text: dealer.name, disabled: true };
    warrantyRoleAllowedLocationsOptions.push(dealerObject);

    const locations: DropdownItemProps[] = [];
    dealer.locations?.forEach(location => location.active && locations.push({ key: location.id, value: location.id, text: location.name }));

    warrantyRoleAllowedLocationsOptions.push(...locations.sort((a, b) => (a.text ?? "").localeCompare(b.text ?? "")));
  });

  return { warrantyRoleAllowedLocationsOptions };
};
